/* eslint-disable max-lines-per-function */
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  PossibleTypesMap,
  split,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from './websocket-link';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { DocumentNode } from 'graphql';
import { isLoggedOut } from './is-logged-out';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { ensureCSRFToken, getCSRFToken } from './csrf-token-lib';
import {
  DefinitionNode,
  NamedTypeNode,
  OperationDefinitionNode,
  TypeNode,
} from 'graphql/language/ast';

// Dynamic WebSocket protocol and hostname detection
const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
const hostname = window.location.hostname;
const port = window.location.port
  ? `:${hostname === 'localhost' ? 3000 : window.location.port}`
  : '';
const wsUri = `${wsProtocol}://${hostname}${port}/graphql`;

const queryIsSubscription = (query: DocumentNode) => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  );
};

const getNamedType = (typeNode: TypeNode): NamedTypeNode => {
  if (typeNode.kind === 'NamedType') return typeNode;
  return getNamedType(typeNode.type);
};

const allowedUpdateIncidentInputKeys = new Set<string>([
  'affectedProducts',
  'areNotificationsSuppressed',
  'areTasksCreated',
  'attachments',
  'cost',
  'criticality',
  'description',
  'deviatingLocation',
  'deviatingReporterContact',
  'deviatingReporterFunction',
  'deviatingReporterName',
  'facts',
  'formInstances',
  'hasDeviatingLocation',
  'incidentLocationNotifications',
  'isCompleted',
  'isTopSecret',
  'locationId',
  'name',
  'occurredAt',
  'onPremise',
  'persons',
  'pressData',
  'rawFacts',
  'reference',
  'scenarios',
  'sourceId',
  'statusId',
  'tags',
  'type',
  'vehicles',
]);

const normalizeAssociationArray = (value: unknown): unknown => {
  if (!Array.isArray(value)) return value;
  return value.map((item) => {
    if (!item || typeof item !== 'object') return item;
    const record = item as Record<string, unknown>;
    if (typeof record.id === 'string') return { id: record.id };
    return item;
  });
};

const normalizeUpdateIncidentInput = (value: unknown): unknown => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const input = value as Record<string, unknown>;

  const output: Record<string, unknown> = {};
  for (const key of Object.keys(input)) {
    if (allowedUpdateIncidentInputKeys.has(key)) {
      output[key] = input[key];
    }
  }

  // Common case: accidentally passing query result shapes (Incident, Source, Location, Status).
  if (output.sourceId === undefined) {
    const source = input.source as unknown;
    if (
      source &&
      typeof source === 'object' &&
      typeof (source as any).id === 'string'
    ) {
      output.sourceId = (source as any).id;
    }
  }
  if (output.locationId === undefined) {
    const location = input.location as unknown;
    if (
      location &&
      typeof location === 'object' &&
      typeof (location as any).id === 'string'
    ) {
      output.locationId = (location as any).id;
    }
  }
  if (output.statusId === undefined) {
    const status = input.status as unknown;
    if (
      status &&
      typeof status === 'object' &&
      typeof (status as any).id === 'string'
    ) {
      output.statusId = (status as any).id;
    }
  }

  output.attachments = normalizeAssociationArray(output.attachments);
  output.scenarios = normalizeAssociationArray(output.scenarios);

  return output;
};

const normalizeUpdateIncidentInputVariables = () =>
  new ApolloLink((operation, forward) => {
    const operationDef = (
      operation.query.definitions as readonly DefinitionNode[]
    ).find((d) => d.kind === 'OperationDefinition') as
      | OperationDefinitionNode
      | undefined;

    const variableDefs = operationDef?.variableDefinitions ?? [];
    for (const variableDef of variableDefs) {
      const typeName = getNamedType(variableDef.type).name.value;
      if (typeName !== 'UpdateIncidentInput') continue;
      const variableName = variableDef.variable.name.value;
      if (!operation.variables || !(variableName in operation.variables))
        continue;
      operation.variables[variableName] = normalizeUpdateIncidentInput(
        operation.variables[variableName],
      );
    }

    return forward ? forward(operation) : null;
  });

// eslint-disable-next-line max-lines-per-function
export const makeLink = () => {
  // Ensure CSRF token is available before making requests
  ensureCSRFToken().catch((error) => {
    console.error('Failed to ensure CSRF token:', error);
  });

  // The HTTP link now dynamically gets the CSRF token for each request
  const httpLink = new UploadHttpLink({
    headers: {
      'Apollo-Require-Preflight': 'true',
      'x-internal-request': 'true',
    },
    uri: '/graphql',
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = localStorage.getItem('token');
    // Get CSRF token dynamically for each request
    const csrfToken = getCSRFToken();

    return token
      ? {
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
            'X-XSRF-TOKEN': csrfToken || '',
          },
        }
      : {
          headers: {
            ...headers,
            'X-XSRF-TOKEN': csrfToken || '',
          },
        };
  });

  // eslint-disable-next-line complexity
  const logoutLink = onError((handler) => {
    const { graphQLErrors, networkError, operation } = handler as any;
    if (graphQLErrors && isLoggedOut(graphQLErrors)) {
      const pathname = window.location.pathname;
      const isExcludedRoute = /^\/(login|confirm|video-call|darksite)/.test(
        pathname,
      );

      if (!isExcludedRoute) {
        localStorage.setItem('redirectAfterLogin', pathname);
        window.location.pathname = '/login';
      }
    }

    if (networkError) {
      // Log network errors but don't reload the page
      // Apollo Client and graphql-ws have built-in retry mechanisms
      const errorMessage = networkError.toString().toLowerCase();
      if (
        errorMessage.includes('epipe') ||
        errorMessage.includes('econnreset') ||
        errorMessage.includes('network')
      ) {
        console.warn(
          '[Apollo] Network error detected, Apollo will retry automatically:',
          errorMessage,
        );
        // Don't reload - let Apollo's retry mechanism handle it
      }
    }
  });

  const wsLink = new WebSocketLink({
    url: wsUri,
    lazy: true,
    retryAttempts: Infinity,
    shouldRetry: () => true,
    retryWait: async (retries) => {
      // Exponential backoff with max of 30 seconds
      const delay = Math.min(1000 * Math.pow(2, retries), 30000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    },
    connectionParams: () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn(
          '[Apollo] No token found in localStorage. Subscription may fail.',
        );
      }
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    on: {
      error: (error: unknown) => {
        const err = error as { message?: string };
        console.error('[Apollo] WebSocket error:', err);
        if (err.message && err.message.includes('Forbidden')) {
          console.error(
            '[Apollo] Access denied to subscription. This may be due to:',
          );
          console.error('[Apollo] 1. Authentication token expired or invalid');
          console.error(
            '[Apollo] 2. User lacks permission for this subscription',
          );
          console.error(
            '[Apollo] 3. Server-side authorization rules blocking access',
          );
          console.debug('[Apollo] WebSocket URL:', wsUri);
          console.debug('[Apollo] Has token:', !!localStorage.getItem('token'));
          // Don't reload page - let the retry mechanism handle reconnection
          // Only redirect to login if it's a clear auth issue
          const pathname = window.location.pathname;
          const isExcludedRoute = /^\/(login|confirm|video-call|darksite)/.test(
            pathname,
          );
          if (!isExcludedRoute && err.message.includes('Forbidden')) {
            console.warn('[Apollo] Redirecting to login due to auth failure');
            localStorage.setItem('redirectAfterLogin', pathname);
            window.location.pathname = '/login';
          }
        }
      },
      connected: () => {
        console.debug('[Apollo] WebSocket connection established successfully');
      },
      connecting: () => {
        console.debug('[Apollo] WebSocket connecting...');
      },
      closed: (event: unknown) => {
        console.warn('[Apollo] WebSocket connection closed:', event);
        // Let graphql-ws handle automatic reconnection via retryAttempts
        // Don't force page reload as this causes poor UX
        if (
          event &&
          typeof event === 'object' &&
          'code' in event &&
          (event as { code: number }).code !== 1000
        ) {
          console.warn(
            '[Apollo] Unexpected WebSocket closure. graphql-ws will automatically retry.',
          );
        }
      },
    },
  });

  const apolloLink = split(
    ({ query }) => {
      const isSub = queryIsSubscription(query);
      if (isSub) {
        console.debug(
          '[Apollo] Routing operation to WebSocketLink (subscription):',
          query,
        );
      }
      return isSub;
    },
    wsLink,
    authLink.concat(httpLink as any),
  );

  return logoutLink
    .concat(removeTypenameFromVariables())
    .concat(normalizeUpdateIncidentInputVariables())
    .concat(apolloLink);
};

// Re-export CSRF token functions
export {
  fetchCSRFToken,
  refreshCSRFToken,
  startCSRFTokenRefresh,
} from './csrf-token-lib';

export const customizedApolloClient = ({
  possibleTypes,
}: {
  possibleTypes: PossibleTypesMap;
}): ApolloClient => {
  const client = new ApolloClient({
    link: makeLink(),
    cache: new InMemoryCache({
      possibleTypes,
      typePolicies: {
        Query: {
          fields: {},
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });

  // Strip deprecated canonizeResults option from cache.diff to avoid Apollo 3.14 error 104
  const originalDiff = (client.cache as any).diff?.bind(client.cache);
  if (originalDiff) {
    (client.cache as any).diff = (options: any) => {
      if (options && 'canonizeResults' in options) {
        const { canonizeResults, ...rest } = options;
        return originalDiff(rest);
      }
      return originalDiff(options);
    };
  }

  client.onResetStore(async () => {
    client.setLink(makeLink());
    // Reapply diff wrapper after reset
    const orig = (client.cache as any).diff?.bind(client.cache);
    if (orig) {
      (client.cache as any).diff = (options: any) => {
        if (options && 'canonizeResults' in options) {
          const { canonizeResults, ...rest } = options;
          return orig(rest);
        }
        return orig(options);
      };
    }
  });

  return client;
};
